import type { JSX } from "react";
import { useEffect, useState } from "react";
import { ulid } from "ulid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, X } from "lucide-react";
import type { RecipeIngredient } from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";

interface Props {
  value: RecipeIngredient[];
  onChange: (next: RecipeIngredient[]) => void;
}

function newIngredient(): RecipeIngredient {
  return {
    id: `ing_${ulid()}`,
    raw: "",
    qty: null,
    unit: null,
    name: "",
    notes: null,
  };
}

/**
 * Parse a quantity string into a number. Accepts:
 *   - Decimals: "0.5", "1.25"
 *   - Plain integers: "2"
 *   - Mixed fractions: "1 1/2", "2 3/4"
 *   - Simple fractions: "1/2", "3/4"
 *   - Unicode fraction glyphs: "½", "¼", "1 ½", "2 ¾"
 * Returns null for empty or unparseable input.
 */
const FRACTION_GLYPHS: Record<string, number> = {
  "½": 0.5, "⅓": 1 / 3, "⅔": 2 / 3, "¼": 0.25, "¾": 0.75,
  "⅕": 0.2, "⅖": 0.4, "⅗": 0.6, "⅘": 0.8,
  "⅙": 1 / 6, "⅚": 5 / 6, "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
};
export function parseQty(text: string): number | null {
  const t = text.trim();
  if (t === "") return null;
  // "1 ½" or "½"
  const glyphMatch = /^(?:(\d+)\s+)?([½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])$/.exec(t);
  if (glyphMatch) {
    const whole = glyphMatch[1] ? Number.parseInt(glyphMatch[1], 10) : 0;
    const frac = FRACTION_GLYPHS[glyphMatch[2] ?? ""];
    if (frac === undefined) return null;
    return whole + frac;
  }
  // "1 1/2"
  const mixedMatch = /^(\d+)\s+(\d+)\/(\d+)$/.exec(t);
  if (mixedMatch) {
    const w = Number.parseInt(mixedMatch[1] ?? "0", 10);
    const n = Number.parseInt(mixedMatch[2] ?? "0", 10);
    const d = Number.parseInt(mixedMatch[3] ?? "1", 10);
    if (d === 0) return null;
    return w + n / d;
  }
  // "1/2"
  const fracMatch = /^(\d+)\/(\d+)$/.exec(t);
  if (fracMatch) {
    const n = Number.parseInt(fracMatch[1] ?? "0", 10);
    const d = Number.parseInt(fracMatch[2] ?? "1", 10);
    if (d === 0) return null;
    return n / d;
  }
  // Plain decimal — but reject anything else parseFloat would accept (e.g. "1abc")
  if (!/^\d+(?:\.\d+)?$/.test(t)) return null;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Quantity input with local text state. The parent stores a canonical number
 * (e.g. 0.5); the user types text (e.g. "1/2" or "½") which we parse on every
 * keystroke. While the parse is failing (e.g. mid-typing "1/"), the typed text
 * is preserved AND the parent's last good value stays untouched — no digits
 * are silently dropped.
 */
function QtyInput({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
}): JSX.Element {
  const [text, setText] = useState<string>(value === null ? "" : String(value));
  // If the parent's value changes externally (e.g. row reset), pull it in —
  // but only when it differs from what our text would parse to. This avoids
  // overwriting in-progress typing like "1/".
  useEffect(() => {
    const parsed = parseQty(text);
    if (parsed !== value) {
      setText(value === null ? "" : String(value));
    }
  }, [value]);
  return (
    <Input
      placeholder="½ or 1/2"
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        const trimmed = next.trim();
        if (trimmed === "") {
          onChange(null);
          return;
        }
        const parsed = parseQty(trimmed);
        if (parsed !== null) onChange(parsed);
        // If parse failed, leave the parent's value alone — the user is
        // probably still typing (e.g. "1/" before "1/2").
      }}
      className="w-24"
      aria-label="Ingredient quantity"
    />
  );
}

function SortableRow({
  item,
  onChangeRow,
  onRemove,
}: {
  item: RecipeIngredient;
  onChangeRow: (next: RecipeIngredient) => void;
  onRemove: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5"
    >
      <button
        type="button"
        className="mt-2 text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing"
        aria-label="Reorder ingredient"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
        <Input
          placeholder="2 lb ripe tomatoes"
          value={item.raw}
          onChange={(e) => { onChangeRow({ ...item, raw: e.target.value }); }}
          className="w-full min-w-0 sm:w-auto sm:flex-1"
          aria-label="Ingredient"
        />
        <QtyInput value={item.qty} onChange={(qty) => { onChangeRow({ ...item, qty }); }} />
        <Input
          placeholder="unit"
          value={item.unit ?? ""}
          onChange={(e) => { onChangeRow({ ...item, unit: e.target.value || null }); }}
          className="w-24"
          aria-label="Ingredient unit"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="mt-2 text-zinc-500 hover:text-destructive p-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
        aria-label="Remove ingredient"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export function IngredientList({ value, onChange }: Props): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = value.findIndex((i) => i.id === active.id);
    const to = value.findIndex((i) => i.id === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(value, from, to));
  }

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={value.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {value.map((item) => (
            <SortableRow
              key={item.id}
              item={item}
              onChangeRow={(next) => { onChange(value.map((v) => (v.id === item.id ? next : v))); }}
              onRemove={() => { onChange(value.filter((v) => v.id !== item.id)); }}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => { onChange([...value, newIngredient()]); }}
      >
        <Plus className="mr-2 h-4 w-4" aria-hidden />
        Add ingredient
      </Button>
    </div>
  );
}
